import React, { useState, useRef, useCallback } from "react";
import { Tooltip, useModal, ModalEvents, Frame, Button } from "@react95/core";
import { useApp } from "../../context/AppContext";
import { useAgent } from "../../context/ClippyContext";
import { useAchievement } from "../../context/AchievementContext";

import { CurvesAndColors100, BlankScreen100 } from "@react95/icons";

export default function DebugTrayIcon() {
  const { status, error } = useApp();
  const { subscribe } = useModal();
  const { showAchievement } = useAchievement();

  const {
    currentAgent,
    isAgentVisible,
    showAgent,
    hideAgent,
    showMessage,
    playAnimation,
    cancelAnimation,
    moveTo,
    gestureAt,
  } = useAgent();

  const [showDebugPanel, setShowDebugPanel] = useState(false);

  const eventLogRef = useRef<HTMLDivElement>(null);
  const eventCountRef = useRef(0);

  // helper function to add events to the display without causing re-renders
  const addEventToLog = useCallback((eventText: string) => {
    if (eventLogRef.current) {
      const eventItem = document.createElement("div");
      eventItem.style.cssText = `
        font-size: 11px;
        padding: 2px 0;
        border-bottom: 1px solid #ddd;
        font-family: monospace;
        word-break: break-all;
        user-select: all;
      `;
      eventCountRef.current += 1;
      eventItem.textContent = `${eventCountRef.current}. ${eventText}`;
      eventLogRef.current.appendChild(eventItem);

      // auto-scroll to bottom
      eventLogRef.current.scrollTop = eventLogRef.current.scrollHeight;

      // keep only last 15 events to prevent memory issues
      if (eventLogRef.current.children.length > 15) {
        const firstChild = eventLogRef.current.firstChild;
        if (firstChild) {
          eventLogRef.current.removeChild(firstChild);
        }
      }
    }
  }, []);

  React.useEffect(() => {
    if (!showDebugPanel) return;

    const unsubscribes = [
      subscribe(ModalEvents.AddModal, ({ id, title }) => {
        addEventToLog(`➕ Added: ${title} (${id})`);
      }),
      subscribe(ModalEvents.RemoveModal, ({ id }) => {
        addEventToLog(`❌ Removed: ${id}`);
      }),
      subscribe(ModalEvents.MinimizeModal, ({ id }) => {
        addEventToLog(`➖ Minimized: ${id}`);
      }),
      subscribe(ModalEvents.RestoreModal, ({ id }) => {
        addEventToLog(`⬆️ Restored: ${id}`);
      }),
      subscribe(ModalEvents.ModalVisibilityChanged, ({ id }) => {
        addEventToLog(`👁️ Focus: ${id}`);
      }),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [showDebugPanel, subscribe, addEventToLog]);

  return (
    <>
      <Tooltip text={"Debug Info"}>
        <div
          onClick={() => {
            setShowDebugPanel(!showDebugPanel);
            if (!showDebugPanel && eventLogRef.current) {
              eventLogRef.current.innerHTML = "";
              eventCountRef.current = 0;
            }
          }}
          style={{
            width: "20px",
            height: "20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!showDebugPanel ? (
            <BlankScreen100 variant="16x16_4" />
          ) : (
            <CurvesAndColors100 variant="16x16_4" />
          )}
        </div>
      </Tooltip>

      {showDebugPanel && (
        <Frame
          position="fixed"
          bottom="40px"
          right="10px"
          display="flex"
          flexDirection="column"
          width="450px"
          height="250px"
          boxShadow="$out"
          bgColor="$material"
          p="$8"
          zIndex={3}
        >
          <Frame as="h4" fontSize="12px" m="$0" mb="$8">
            Agent Debug
          </Frame>
          <Frame mb="$8" fontSize="11px">
            Status: {currentAgent || "No Agent Loaded"} | Visible:{" "}
            {isAgentVisible ? "Yes" : "No"}
          </Frame>

          <Frame display="flex" gap="$4" mb="$8">
            <Button
              onClick={() => {
                if (isAgentVisible) {
                  hideAgent();
                } else {
                  showAgent(currentAgent || "Clippy", false);
                }
              }}
            >
              {isAgentVisible ? "Hide Agent" : "Show Agent"}
            </Button>
            <Button
              onClick={() => {
                showMessage("Hello from Debug Panel!", 3000);
              }}
            >
              Show Message
            </Button>
            <Button
              onClick={() => {
                playAnimation();
              }}
            >
              Play Animation
            </Button>
            <Button
              onClick={() => {
                cancelAnimation();
              }}
            >
              Cancel Animation
            </Button>
          </Frame>

          <Frame display="flex" gap="$4" mb="$8">
            <Button
              onClick={() => {
                showAchievement({
                  id: "test_achievement",
                  title: "Test Achievement! ✨",
                  description: "This is a test notification",
                  timeout: 5000,
                });
              }}
            >
              Show Test Achievement
            </Button>
          </Frame>

          <Frame as="h4" fontSize="12px" m="$0" mb="$8">
            Modal Events
          </Frame>
          <Frame
            boxShadow="$in"
            bgColor="white"
            p="$8"
            ref={eventLogRef}
            overflow="auto"
            backgroundColor="#fafafa"
            flexGrow={1}
            fontSize="11px"
          />
        </Frame>
      )}
    </>
  );
}
